import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { PrivacyConsent } from "@/components/PrivacyConsent";
import { validateImageFile, sanitizeFilename } from "@/lib/validation";

interface UploadPhotoProps {
  onPhotoUploaded: (url: string) => void;
  onAnalysisComplete: (materials: any) => void;
}

export const UploadPhoto = ({ onPhotoUploaded, onAnalysisComplete }: UploadPhotoProps) => {
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const hasConsent = localStorage.getItem("snapbill_privacy_consent") === "true";

  const processFile = async (file: File) => {
    setUploading(true);
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    try {
      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const { data: { user } } = await supabase.auth.getUser();

      // Only upload to storage if user is authenticated
      let photoUrl = previewUrl;
      if (user) {
        // Sanitize and create unique filename
        const fileExt = file.name.split(".").pop() || "jpg";
        const sanitizedName = sanitizeFilename(file.name.replace(/\.[^/.]+$/, ""));
        const fileName = `${user.id}/${Date.now()}-${sanitizedName}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("photos")
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("photos")
          .getPublicUrl(fileName);
        
        photoUrl = publicUrl;
      }

      onPhotoUploaded(photoUrl);
      
      // Analyze with AI
      setAnalyzing(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        
        const { data, error } = await supabase.functions.invoke("analyze-photo", {
          body: { imageBase64: base64.split(",")[1] },
        });

        if (error) {
          setAnalyzing(false);
          toast({
            title: "Unable to analyze photo",
            description: error.message || "Please upload a photo showing the work area, materials, or construction site.",
            variant: "destructive",
          });
          return;
        }

        // Validate AI response
        if (!data || !data.materials || data.materials.length === 0) {
          setAnalyzing(false);
          toast({
            title: "Unable to analyze photo",
            description: data?.error || "Please upload a photo showing the work area, materials, or construction site. The AI needs to see elements related to the job.",
            variant: "destructive",
          });
          return;
        }

        onAnalysisComplete(data);
        setAnalyzing(false);
        toast({ 
          title: "Analysis complete!", 
          description: `Found ${data.materials.length} items. Review and edit the quote below.` 
        });
      };
      reader.readAsDataURL(file);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setAnalyzing(false);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Check for privacy consent
    if (!hasConsent) {
      setPendingFile(file);
      setShowConsent(true);
      return;
    }

    await processFile(file);
  }, [hasConsent]);

  const handleConsentAccept = async () => {
    setShowConsent(false);
    if (pendingFile) {
      await processFile(pendingFile);
      setPendingFile(null);
    }
  };

  const handleConsentDecline = () => {
    setShowConsent(false);
    setPendingFile(null);
    toast({
      title: "Upload cancelled",
      description: "Photo upload requires consent to process data.",
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp"] },
    maxFiles: 1,
  });

  return (
    <>
      <PrivacyConsent
        open={showConsent}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
      />
      <Card>
        <CardContent className="pt-6">
          <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
          }`}
        >
          <input {...getInputProps()} />
          {uploading || analyzing ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {uploading ? "Uploading photo..." : "Analyzing with AI..."}
              </p>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
              <Button variant="outline" size="sm" onClick={() => setPreview(null)}>
                Upload different photo
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center gap-4">
                <Camera className="h-12 w-12 text-muted-foreground" />
                <Upload className="h-12 w-12 text-muted-foreground" />
              </div>
              <div>
                <p className="text-lg font-semibold">Upload a job photo</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Drag & drop or click to select a photo of the job site
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </>
  );
};
